// Thin wrapper around DOMPurify so every dangerouslySetInnerHTML call site in
// the app sanitizes untrusted/CMS-sourced HTML the same way.
//
// Next.js pages router renders components on both the server (SSR/SSG) and
// the client, but plain `dompurify` needs a real `window`/`document` and
// throws when required in a Node context with no DOM. `isomorphic-dompurify`
// wraps `dompurify` with a jsdom-backed implementation on the server and the
// browser's native DOM on the client (via its package.json "browser" field),
// so this file is safe to import from both server-rendered pages and
// client-only components.
import DOMPurify from "isomorphic-dompurify";

const HTML_NAMESPACE = "http://www.w3.org/1999/xhtml";

// Hosts an embedded <iframe> may point at (exact host or any subdomain of it).
// `path` narrows a general-purpose host to its embed endpoints. Anything else
// (unknown host, relative URL, http:, javascript:, data:, srcdoc) is dropped.
const IFRAME_ALLOWLIST = [
  { host: "youtube.com" },
  { host: "youtube-nocookie.com" },
  { host: "player.vimeo.com" },
  { host: "maps.google.com" },
  { host: "www.google.com", path: "/maps" },
  // 24ttl syndicated product content (content., content2., mx.content. ...)
  { host: "24ttl.stream" },
];

// Hosts a <link rel="stylesheet"> may load from. Every other <link> is dropped.
const STYLESHEET_ALLOWLIST = [
  { host: "fonts.googleapis.com" },
  { host: "24ttl.stream" },
];

// Payment-widget tags that CMS copy may embed as plain markup; the vendor
// scripts the storefront already loads upgrade them. Only these names and
// these attribute names survive (no on* handlers, no URL-valued attributes).
const CUSTOM_ELEMENT_HANDLING = {
  tagNameCheck: /^(tamara-widget|mispay-widget)$/,
  attributeNameCheck:
    /^(type|amount|lang|currency|country|inline-type|inline-variant)$/,
  allowCustomizedBuiltInElements: false,
};

// Link targets that keep navigation in the current browsing context; every
// other target (_blank, named windows) gets rel="noopener noreferrer".
const SAME_CONTEXT_TARGETS = ["", "_self", "_parent", "_top"];

// DOMPurify does not parse CSS. Modern browsers ignore script URLs in CSS, but
// drop inline styles carrying legacy script-in-CSS constructs anyway.
const UNSAFE_INLINE_STYLE = /javascript:|vbscript:|expression\s*\(|-moz-binding|behavior\s*:/i;

// "<", ">" and "&" as JSON unicode escapes: same data for any JSON parser, and
// the text can no longer close or re-open markup when the HTML is re-parsed.
const JSON_TEXT_ESCAPES = { "<": "\\u003c", ">": "\\u003e", "&": "\\u0026" };

const SANITIZE_CONFIG = {
  // Magento PageBuilder emits its <style> block as the first node of the
  // fragment; without FORCE_BODY the parser hoists it into <head> and it is lost.
  FORCE_BODY: true,
  // script/link are admitted here only so the uponSanitizeElement hook below
  // can decide per node; it removes every script that is not inert JSON-LD and
  // every link that is not an allowlisted stylesheet.
  ADD_TAGS: ["iframe", "script", "link"],
  ADD_ATTR: [
    "target",
    "allow",
    "allowfullscreen",
    "frameborder",
    "loading",
    "referrerpolicy",
  ],
  FORBID_TAGS: ["form", "object", "embed"],
  CUSTOM_ELEMENT_HANDLING,
};

function isAllowedHttpsUrl(value, allowlist) {
  if (!value) return false;
  let url;
  try {
    // Protocol-relative URLs (//www.youtube.com/...) resolve to https.
    url = new URL(value, "https://invalid.invalid");
  } catch (e) {
    return false;
  }
  if (url.protocol !== "https:") return false;
  const hostname = url.hostname.toLowerCase();
  return allowlist.some(
    ({ host, path }) =>
      (hostname === host || hostname.endsWith(`.${host}`)) &&
      (!path || url.pathname === path || url.pathname.startsWith(`${path}/`))
  );
}

function removeNode(node) {
  node.parentNode?.removeChild(node);
}

function keepOnlyAttributes(node, names) {
  Array.from(node.attributes).forEach(({ name }) => {
    if (!names.includes(name.toLowerCase())) node.removeAttribute(name);
  });
}

// Runs before DOMPurify's own checks on each element.
function sanitizeElement(node, { tagName }) {
  if (tagName === "script") {
    // Only structured data survives. It is never executed: the type is not a
    // script type, and innerHTML-inserted scripts do not run anyway.
    const type = (node.getAttribute("type") || "").trim().toLowerCase();
    if (node.namespaceURI !== HTML_NAMESPACE || type !== "application/ld+json") {
      removeNode(node);
      return;
    }
    keepOnlyAttributes(node, ["type"]);
    node.setAttribute("type", "application/ld+json");
    node.textContent = node.textContent.replace(
      /[<>&]/g,
      (ch) => JSON_TEXT_ESCAPES[ch]
    );
    return;
  }

  if (tagName === "link") {
    const rel = (node.getAttribute("rel") || "").trim().toLowerCase();
    if (
      node.namespaceURI !== HTML_NAMESPACE ||
      rel !== "stylesheet" ||
      !isAllowedHttpsUrl(node.getAttribute("href"), STYLESHEET_ALLOWLIST)
    ) {
      removeNode(node);
      return;
    }
    keepOnlyAttributes(node, ["rel", "href", "media"]);
    return;
  }

  if (tagName === "style") {
    // Markup-like text in CSS (comments like "/* <section> */", or the "<!--"
    // wrappers pasted from Office) makes DOMPurify drop the whole block. CSS
    // ignores "<!--"/"-->" and "\3c " is the CSS escape for "<", so the
    // stylesheet keeps its meaning. Only a "<" that could start a tag, end tag
    // or comment is rewritten; comparisons like "(width <= 768px)" stay as-is.
    node.textContent = node.textContent
      .replace(/<!--|-->/g, "")
      .replace(/<(?=[/\w!])/g, "\\3c ");
  }
}

// Config and hooks are applied once per DOMPurify instance (this module is
// evaluated once per server process / browser bundle), so sanitize() does not
// re-parse the config on every call. This wrapper is the only importer of
// isomorphic-dompurify. The hooks are idempotent, so a repeat registration
// (dev HMR) cannot change the output.
DOMPurify.setConfig(SANITIZE_CONFIG);
DOMPurify.addHook("uponSanitizeElement", sanitizeElement);
DOMPurify.addHook("uponSanitizeAttribute", (node, data) => {
  if (data.attrName === "style" && UNSAFE_INLINE_STYLE.test(data.attrValue)) {
    data.keepAttr = false;
  }
});
DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  if (node.nodeType !== 1) return;

  if (node.nodeName === "IFRAME") {
    if (!isAllowedHttpsUrl(node.getAttribute("src"), IFRAME_ALLOWLIST)) {
      removeNode(node);
      return;
    }
  }

  if (node.hasAttribute("target")) {
    const target = (node.getAttribute("target") || "").trim().toLowerCase();
    if (!SAME_CONTEXT_TARGETS.includes(target)) {
      const rel = (node.getAttribute("rel") || "").split(/\s+/).filter(Boolean);
      const relLower = rel.map((token) => token.toLowerCase());
      ["noopener", "noreferrer"].forEach((token) => {
        if (!relLower.includes(token)) rel.push(token);
      });
      node.setAttribute("rel", rel.join(" "));
    }
  }
});

/**
 * Sanitize an HTML string before rendering it via dangerouslySetInnerHTML.
 * Strips executable scripts, inline event handlers (onerror, onclick, ...),
 * javascript:/data: URLs (data: stays allowed on media tags only), <form>,
 * <object>/<embed>, and iframes / stylesheet links whose host is not
 * allowlisted. Keeps <style> blocks (incl. PageBuilder's leading one),
 * JSON-LD structured data, the allowlisted payment-widget tags, data-*
 * attributes and link targets (target=_blank forced to rel="noopener noreferrer").
 *
 * @param {string} html - raw HTML (e.g. CMS content, product/category description)
 * @returns {string} sanitized HTML safe to render
 */
export function sanitizeHtml(html) {
  if (html === null || html === undefined || html === "") {
    return "";
  }

  return DOMPurify.sanitize(String(html));
}

export default sanitizeHtml;
